/* eslint-disable */
import React, { lazy, Suspense, useMemo, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import OneSignal from 'react-onesignal';
import { Provider, ErrorBoundary } from '@rollbar/react'; // Provider imports 'rollbar'
import Context from './context/context';
import ScrollToTop from './components/ScrollToTop';
import CustomSwitch from './components/CustomSwitch';
import Navigation from './V2/components/Navigation';
import FooterNavigation from './components/FooterNavigation';
import Footer from './V2/components/Footer';
import PageNotFound from './views/pages/PageNotFound';
import Interests from './components/Interests';
import WorthHearing from './V2/pages/NewPages/WorthHearing';
import WomansPage from './V2/pages/NewPages/WomansPage';
import NewTitles from './V2/pages/NewPages/NewTitles';

// Admin / State
import useGlobalState from './store/useGlobalState';
import translationState from './store/translationState';
import { ThemeProvider } from './context/ThemeContext';
// import LanguageSelector from './components/languageSelector.js';

// Auth (new single-key session auth)
import { AuthProvider, useAuth } from './auth/AuthContext';
import RequireAuth from './auth/RequireAuth';

// Styles
import './App.css';
import Testimonies from './views/pages/Testimonies.js';
import Loading from './components/misc/Loading.js';

import Intercom from '@intercom/messenger-js-sdk';
import ReferralRedirect from './views/pages/Referral.tsx';

function NavChrome() {
  const { pathname } = useLocation();
  const hideNav = pathname.startsWith('/auth'); // hide only on /signin
  if (hideNav) return null;
  return <Navigation />;
}

// Pages (lazy)
const Home = lazy(() => import('./V2/Home.tsx'));
const StripeSingle = lazy(() => import('./components/Payment/StripeSingle'));
const History = lazy(() => import('./V2/pages/History'));
const SignIn = lazy(() => import('./V2/pages/Auth'));
const KcSignInProcess = lazy(
  () => import('./V2/pages/Auth/KcSignInProcess.tsx'),
);
const KcSignUpProcess = lazy(
  () => import('./V2/pages/Auth/KcSignUpProcess.jsx'),
);
const KcLinking = lazy(() => import('./V2/pages/Auth/Link.tsx'));
const SignUp = lazy(() => import('./views/pages/SignUp'));
const AnalyticsReon = lazy(() => import('./views/pages/AnalyticsReon.js'));

const PCDL_AFFILIATE_HOME = lazy(() => import('./V2/pages/Affiliate/Home.tsx'));
const PCDL_AFFILIATE_EVERYDAYSOLUTIONS = lazy(
  () => import('./V2/pages/Affiliate/EverydaySolutions.tsx'),
);
const PCDL_AFFILIATE_ONBOARDING = lazy(
  () => import('./V2/pages/Affiliate/Onboarding.tsx'),
);

const PCDL_AFFILIATE_COMPLETION = lazy(
  () => import('./V2/pages/Affiliate/Completion.tsx'),
);

const PCDL_AFFILIATE_UPDATE = lazy(
  () => import('./V2/pages/Affiliate/Profile.tsx'),
);
const PCDL_AFFILIATE_DASHBOARD = lazy(
  () => import('./V2/pages/Affiliate/Dashboard.tsx'),
);
const PCDL_AFFILIATE_TERMS = lazy(
  () => import('./V2/pages/Affiliate/Terms_Standalone.tsx'),
);

const PCDL_AFFILIATE_WATCH = lazy(
  () => import('./V2/pages/Affiliate/Watch.tsx'),
);

const PCDL_AFFILIATE_TRACKING = lazy(
  () => import('./V2/pages/Affiliate/LinkTracking.tsx'),
);

const AffiliateLanding = lazy(() => import('./views/pages/AfilliateLanding'));
const AffiliateSignUp = lazy(() => import('./views/pages/AffiliateSignUp'));
const PCO_FN_REGISTRATION = lazy(() => import('./V2/Register.tsx'));

const Register = lazy(() => import('./views/pages/Registration'));
const Plans = lazy(() => import('./V2/pages/Plans.jsx'));
const Espees = lazy(() => import('./views/pages/Espees'));
const Results = lazy(() => import('./V2/pages/Results.jsx'));
const Special = lazy(() => import('./views/pages/Special'));
const PCDL_1_BILLION_SOLUTIONS = lazy(
  () => import('./views/pages/1BillionSolutions/home'),
);
const COURSE_BUOM = lazy(
  () => import('./V2/pages/Courses/basicUnderstandingOfMoney.jsx'),
);
const PCDL_SOLUTIONS = lazy(() => import('./V2/Solutions/Home.jsx'));
const PCDL_SOLUTIONS_SEARCH = lazy(() => import('./V2/Solutions/Search.jsx'));
const PCDL_SOLUTIONS_PLAYER = lazy(() => import('./V2/Solutions/Player.jsx'));
const PCDL_SEARCH = lazy(() => import('./views/pages/Search/Home'));
const AFFILIATE = lazy(() => import('./views/pages/Affiliate/Home'));
const CertificateGenerator = lazy(() => import('./views/pages/CertificateGen'));
const AFFILIATE_NEW = lazy(
  () => import('./views/pages/Affiliate/selectgrouplink'),
);
const AFFILIATE_WORDFEST = lazy(
  () => import('./views/pages/Affiliate/Wordfest'),
);
const WordfestAdmin = lazy(() => import('./views/pages/Wordfest/Admin'));
const IPPCAdmin = lazy(() => import('./views/pages/IPPC/Admin'));
const WordAtWorkAdmin = lazy(
  () => import('./views/pages/IPPC/Admin-WordAtWork'),
);
const MaintenancePage = lazy(() => import('./V2/Maintenance.tsx'));
const ResetPassword = lazy(() => import('./views/pages/ResetPassword'));
const Terms = lazy(() => import('./views/pages/Terms'));
const HavenTerms = lazy(() => import('./views/pages/Haven_Terms'));
const SWCTerms = lazy(() => import('./views/pages/SWC_Terms'));
const HSLHSTerms = lazy(() => import('./views/pages/HSLHS2_Terms'));
const SERaffleTerms = lazy(() => import('./views/pages/Raffle_Terms'));
const SERaffleAdmin = lazy(() => import('./views/pages/Raffle_Admin'));
const REONTerms = lazy(() => import('./views/pages/REON_Terms'));
const DeleteAccount = lazy(() => import('./views/pages/DeleteAccount'));
const MTCTerms = lazy(() => import('./views/pages/3MTC_Terms'));
const ICLCTerms = lazy(() => import('./views/pages/ICLC_Terms'));
const ChristmasOffer = lazy(() => import('./views/pages/ChristmasOffer/Home'));
const ChristmasOfferTerms = lazy(() => import('./views/pages/Christmas_Terms'));
const IPPCTerms = lazy(() => import('./views/pages/IPPC_Terms'));
const PROMO_ISM = lazy(() => import('./views/pages/Payment/Promo/ISM'));
const COUPON_CODES = lazy(() => import('./views/pages/CouponCodes'));
const ITPLC_REDEEM = lazy(() => import('./views/pages/ItplcRedeem'));

const PROMO_LAGOSZ6 = lazy(() => import('./views/pages/Payment/Promo/LagosZ6'));
const Wordfest_Analytics = lazy(
  () => import('./views/pages/Wordfest/Analytics'),
);
const Wordfest_Analytics_group = lazy(
  () => import('./views/pages/Wordfest/Analytics_group'),
);

const PROMO_SWC = lazy(() => import('./views/pages/Payment/Promo/SWC'));
const PROMO_CEMESEASIA = lazy(
  () => import('./views/pages/Payment/Promo/PROMO_CEMESEASIA'),
);
const Privacy = lazy(() => import('./views/pages/Privacy'));
const Testimony = lazy(() => import('./views/templates/Testimony'));
const Documentary = lazy(() => import('./views/templates/Documentary'));

const NewPassword = lazy(() => import('./views/pages/NewPassword'));

const PCDL4KIDS_HOME = lazy(() => import('./views/pages/PCDL4Kids/Home'));
const Quiz = lazy(() => import('./views/pages/Quiz'));
const Promotionals = lazy(() => import('./views/pages/Promotionals'));

const QuizTemplate = lazy(() => import('./views/templates/Quiz'));
const Dictionary = lazy(() => import('./V2/pages/Dictionary/Dictionary.jsx'));
const DictionaryTerm = lazy(() => import('./V2/pages/Dictionary/Video.jsx'));
const DailyParticipation = lazy(
  () => import('./views/pages/DailyParticipation'),
);
const DOM_AVATAR = lazy(() => import('./views/pages/Dom_avatar'));
const PRISON_AVATAR = lazy(() => import('./views/pages/Prison_avatar'));
const KINGDOM_AVATAR = lazy(() => import('./views/pages/Kingdom_avatar'));

const DOM_2026_AVATAR = lazy(() => import('./views/pages/8dom_avatar_2026'));

const WORDFEST_AVATAR = lazy(() => import('./views/pages/Wordfest_avatar'));
const AVATARS = lazy(() => import('./views/pages/avatars'));
const PCDLTV = lazy(() => import('./views/pages/PcdlTv'));
const Captions = lazy(() => import('./views/pages/Captions/Captions.js'));
const Transcript = lazy(() => import('./views/pages/Captions/Transcript.js'));
const NGNCAMPAIGN = lazy(() => import('./views/pages/ngnCampaign.jsx'));

const ChristmasParticipation = lazy(
  () => import('./views/pages/ChristmasParticipation'),
);

const Complete = lazy(() => import('./views/pages/Complete'));
const Payment = lazy(() => import('./views/pages/Payment/Payment'));
const PaymentV2 = lazy(() => import('./views/pages/PaymentV2/Payment'));
const PCDL_Subscription = lazy(() => import('./V2/pages/Subscription'));
const PCDL_Subscription_Special = lazy(
  () => import('./views/pages/PaymentV2/SubscriptionSpecial.js'),
);
const PaymentV2_HSLHS = lazy(
  () => import('./views/pages/PaymentV2/HSLHS_Payment'),
);
const PaymentV2_REON = lazy(() => import('./views/pages/PaymentV2/REON'));
const PaymentV2SA = lazy(() => import('./views/pages/PaymentV2SA/Payment'));
const PaymentOneTime = lazy(() => import('./views/pages/Payment/OneTime'));
const PROMO_WORD_AT_WORK = lazy(
  () => import('./views/pages/Payment/Promo/word_at_work'),
);
const EXTERNAL_CAMPAIGN = lazy(() => import('./views/pages/externalCampaign'));
const EXTERNAL_CAMPAIGN2 = lazy(
  () => import('./views/pages/externalCampaign_offer.tsx'),
);

const YLWS = lazy(() => import('./views/pages/Payment/Promo/YLWS'));
const Haven_Redeem = lazy(() => import('./views/pages/HavenRedeem'));
const MOT_Redeem = lazy(() => import('./views/pages/4MTCRedeem'));

const Haven_Certificate = lazy(
  () => import('./views/pages/HavenCertificateGen'),
);

const Ten_Thoughts_Certificate = lazy(
  () => import('./views/pages/10thoughtCertificateGen'),
);
const Coupon_Redeem = lazy(() => import('./views/pages/CouponRedeem'));
const Coupon_Fetch = lazy(() => import('./views/pages/CouponFetch'));
const Help = lazy(() => import('./V2/pages/HelpCenter.tsx'));

const SeriesYLWS = lazy(() => import('./V2/pages/Series-YLWS.jsx'));

const Categories = lazy(() => import('./V2/pages/Categories'));
const Languages = lazy(() => import('./V2/pages/Languages.tsx'));
// const SelectGroupLink = lazy(() => import('./views/pages/SelectGroupLink'));
const Language = lazy(() => import('./V2/Language.tsx'));
const Category = lazy(() => import('./V2/pages/Category'));
const SpecialWatch = lazy(() => import('./views/templates/SpecialWatch'));
const Watch = lazy(() => import('./V2/pages/Watch.jsx'));
const Landing = lazy(() => import('./V2/Landing.jsx'));
const Listen = lazy(() => import('./V2/pages/Listen.jsx'));
const Series = lazy(() => import('./V2/pages/Series.jsx'));

const Expose = lazy(() => import('./V2/pages/Expose.tsx'));

const Sodl = lazy(() => import('./views/pages/sodl'));
const SodlV2 = lazy(() => import('./views/pages/sodlv2'));

const Settings = lazy(() => import('./V2/pages/Settings.jsx'));
const Browse = lazy(() => import('./V2/pages/Browse'));
const Library = lazy(() => import('./views/pages/Library'));
const Raffle = lazy(() => import('./views/pages/Raffle/Home'));
const IPPC_OFFER = lazy(() => import('./views/pages/IPPC_OFFER/Home'));
const CHALLENGE_4MOT = lazy(() => import('./views/pages/4MOT/Home'));
const CHALLENGE_4MOT_WATCH = lazy(() => import('./views/pages/4MOT/Video'));

const Wordfest = lazy(() => import('./views/pages/Wordfest/Home'));
const Wordfest_Testimony = lazy(
  () => import('./views/pages/Wordfest/Testimonies'),
);

const Wordfest_Special = lazy(() => import('./views/pages/Wordfest/Special'));
const Wordfest_Quiz = lazy(() => import('./views/pages/Wordfest/Quiz'));
const Wordfest_Quiz_Special = lazy(
  () => import('./views/pages/Wordfest/Quiz_Special'),
);
const Wordfest_Quiz_App = lazy(() => import('./views/pages/Wordfest/QuizApp'));
const Challenges_Quiz_App = lazy(
  () => import('./views/pages/Challenges/QuizApp'),
);
const DOM_VIDEO = lazy(() => import('./views/pages/8dom/Video'));

const Wordfest_Video = lazy(() => import('./views/pages/Wordfest/Video'));
const Wordfest_Video_Special = lazy(
  () => import('./views/pages/Wordfest/Video_Special'),
);

const Wordfest_Teens = lazy(() => import('./views/pages/Wordfest/Teens/Home'));
const Wordfest_Teens_Quiz = lazy(
  () => import('./views/pages/Wordfest/Teens/Quiz'),
);
const Wordfest_Teens_Quiz_App = lazy(
  () => import('./views/pages/Wordfest/Teens/QuizApp'),
);
const Wordfest_Teens_Video = lazy(
  () => import('./views/pages/Wordfest/Teens/Video'),
);

const Wordfest_Kids = lazy(() => import('./views/pages/Wordfest/Kids/Home'));
const Wordfest_Kids_Quiz = lazy(
  () => import('./views/pages/Wordfest/Kids/Quiz'),
);
const Wordfest_Kids_Quiz_App = lazy(
  () => import('./views/pages/Wordfest/Kids/QuizApp'),
);
const Wordfest_Kids_Video = lazy(
  () => import('./views/pages/Wordfest/Kids/Video'),
);

const Watchlist = lazy(() => import('./V2/pages/Watchlist.jsx'));
const Playlist_Create = lazy(() => import('./V2/pages/Playlist/Create.jsx'));

const PCDL_SOLUTIONS_SPECIALSTS = lazy(
  () => import('./views/pages/SolutionsSpecialist.js'),
);

const Checkout = lazy(() => import('./views/pages/Checkout'));
const CheckoutV2 = lazy(() => import('./views/pages/CheckoutV2'));
const CheckoutV3 = lazy(() => import('./views/pages/CheckoutV3'));
const PCDL_Checkout = lazy(() => import('./views/pages/PCDL_Checkout'));
const Wallet = lazy(() => import('./V2/pages/Wallet.jsx'));

const Wordfest_Extravaganza = lazy(
  () => import('./views/pages/Wordfest/Extravaganza.js'),
);

const Video = lazy(() => import('./views/templates/Video'));
const Audio = lazy(() => import('./views/templates/Audio'));

const MTC = lazy(() => import('./views/pages/3MTC'));

const UPDATEUSERCODE = lazy(() => import('./views/pages/updateUserCode'));

const HTTN = lazy(() => import('./views/pages/Payment/Promo/HTTN'));
const ICLC_MBTC = lazy(() => import('./views/pages/Payment/Promo/ICLC_MBTC'));

const REGISTER_CODE = lazy(() => import('./views/pages/RegisterCode'));
const REGISTER_CODE2 = lazy(() => import('./views/pages/RegisterCode2'));
const REGISTER_CODE_TEENS = lazy(
  () => import('./views/pages/RegisterCodeTeens'),
);
const REGISTER_AFFILIATE = lazy(
  () => import('./views/pages/RegisterCodeAffiliate'),
);

const REGISTER_CODE_KIDS = lazy(() => import('./views/pages/RegisterCodeKids'));
const PCDL_OFFER = lazy(() => import('./views/pages/Payment/Promo/PCDL_OFFER'));
const MTC_OFFER = lazy(() => import('./views/pages/Payment/Promo/3MTC'));

const rollbarConfig = {
  accessToken: '9a359f4d79c84cadafa7cb026283522d',
  environment: 'production',
};

async function runOneSignal() {
  await OneSignal.init({
    appId: 'ff911853-3e4a-4a17-b079-cac47e91d1ec',
    allowLocalhostAsSecureOrigin: true,
  });
  OneSignal.showSlidedownPrompt();
}

// --- keep this above AppInner (same file) ---
// wherever your refreshUserData() lives
function logoutUser() {
  // clear session + notify app
  localStorage.removeItem('pcdl.store');
  setTimeout(() => {
    window.location.replace('/');
  }, 500);
}

async function refreshUserData() {
  const raw = localStorage.getItem('pcdl.store');
  if (!raw) return;

  let session;
  try {
    session = JSON.parse(raw);
  } catch {
    return;
  }

  const { email, token } = session || {};
  if (!email || !token) return;

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_PCDLSUB_C}/checksubscriptionstatus`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ email, token }),
      },
    );

    // If server actually responds with HTTP 413, log out immediately
    if (res.status === 413) {
      logoutUser();
      return;
    }

    const data = await res.json().catch(() => null);

    // If payload signals 413, also log out
    if (data?.statusCode === 413) {
      logoutUser();
      return;
    }

    // Proceed only on success
    if (data?.statusCode !== 200 || !data?.body) return;

    const body = data.body;
    const next = {
      ...session,
      subscription_name: body.subscription_name,
      wallet_balance: body.wallet_balance,
      wallet_address: body.wallet_address ?? session.wallet_address,
    };

    const changed =
      next.subscription_name !== session.subscription_name ||
      next.wallet_balance !== session.wallet_balance ||
      next.wallet_address !== session.wallet_address;

    if (changed) {
      localStorage.setItem('pcdl.store', JSON.stringify(next));
      window.dispatchEvent(new Event('pcdl:session-updated'));
    }
  } catch {
    // no-op
  }
}

function AppInner() {
  const store = useGlobalState();
  const translation = translationState();
  const { isAuthenticated } = useAuth();

  // run once on mount (covers hard refresh when already logged in)
  useEffect(() => {
    if (localStorage.getItem('pcdl.store')) {
      refreshUserData();
    }
  }, []);

  // also run whenever auth flips to true during this session
  useEffect(() => {
    if (isAuthenticated && localStorage.getItem('pcdl.store')) {
      refreshUserData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    runOneSignal();
  }, []);

  function External() {
    window.location.href = 'https://pcdlsub.loveworldapis.com/gift/claim/wmc';
    return null;
  }

  return (
    <Provider config={rollbarConfig}>
      <ErrorBoundary>
        <Context.Provider value={useMemo(() => ({ store, translation }))}>
          <ThemeProvider>
            <Router>
              <ScrollToTop />
              <NavChrome />

              <div className="w-full min-h-screen">
                <Suspense fallback={<Loading />}>
                  <CustomSwitch>
                    {/* Auth */}
                    <Route
                      path="auth/*"
                      element={
                        isAuthenticated ? (
                          <Navigate to="/" replace />
                        ) : (
                          <SignIn />
                        )
                      }
                    />
                    <Route
                      path="signup/*"
                      element={isAuthenticated ? <SignIn /> : <SignUp />}
                    />

                    {/* Public redirects / promos */}
                    <Route
                      path="/signin"
                      element={<Navigate to="/auth" replace />}
                    />
                    <Route
                      path="/kc/signin/process"
                      element={
                        isAuthenticated ? (
                          <Navigate to="/" replace />
                        ) : (
                          <KcSignInProcess />
                        )
                      }
                    />
                    <Route
                      path="/kc/signup/process"
                      element={
                        isAuthenticated ? (
                          <Navigate to="/" replace />
                        ) : (
                          <KcSignUpProcess />
                        )
                      }
                    />
                    <Route
                      path="/kc/link"
                      element={
                        isAuthenticated ? (
                          <Navigate to="/" replace />
                        ) : (
                          <KcLinking />
                        )
                      }
                    />
                    <Route
                      path="/gdopoffer"
                      element={
                        <Navigate to="/subscription/gdop?promo=gdop" replace />
                      }
                    />
                    <Route
                      path="/gsoffer"
                      element={
                        <Navigate
                          to="/subscription?promo=gsoffer_ngn"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/ismgmc"
                      element={
                        <Navigate to="/subscription?promo=ismgmc_ngn" replace />
                      }
                    />
                    <Route
                      path="/llcoffer"
                      element={
                        <Navigate to="/subscription?promo=llc_ngn" replace />
                      }
                    />
                    <Route
                      path="/easteroffer"
                      element={
                        <Navigate to="/subscription?promo=easter_ngn" replace />
                      }
                    />
                    <Route
                      path="/iclc2025offer"
                      element={
                        <Navigate to="/subscription?promo=iclc_ngn" replace />
                      }
                    />
                    <Route
                      path="/specialoffer"
                      element={
                        <Navigate
                          to="/subscription?promo=specialoffer"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/staffweek2025offer"
                      element={
                        <Navigate to="/subscription?promo=isw_ngn" replace />
                      }
                    />

                    {/* Public routes */}
                    <Route exact path="/privacy_policy" element={<Privacy />} />
                    <Route
                      exact
                      path="/resetpassword"
                      element={<ResetPassword />}
                    />
                    <Route
                      exact
                      path="/forgotpassword/reset/:code"
                      element={<NewPassword />}
                    />
                    <Route
                      exact
                      path="/stripe-single"
                      element={<StripeSingle />}
                    />
                    <Route exact path="/browse" element={<Browse />} />
                    <Route exact path="/plans" element={<Plans />} />
                    <Route
                      path="/subscriptions"
                      element={<Navigate to="/plans" replace />}
                    />
                    <Route exact path="/welcome" element={<Landing />} />
                    <Route
                      path="/everydaysolutions"
                      element={<PCDL_SOLUTIONS />}
                    />
                    <Route
                      path="/everydaysolutions/dictionary"
                      element={<Dictionary />}
                    />
                    <Route
                      path="/everydaysolutions/dictionary/:id"
                      element={<DictionaryTerm />}
                    />
                    <Route
                      path="/everydaysolutions/search"
                      element={<PCDL_SOLUTIONS_SEARCH />}
                    />
                    <Route
                      path="/wordfest/extravaganza"
                      element={<Wordfest_Extravaganza />}
                    />
                    <Route exact path="/results" element={<Results />} />
                    <Route path="/8dom/avatar" element={<DOM_AVATAR />} />
                    <Route
                      path="/8dom_2026/avatar"
                      element={<DOM_2026_AVATAR />}
                    />
                    <Route
                      path="/10thoughtsoncompletion/avatar"
                      element={<PRISON_AVATAR />}
                    />
                    <Route
                      path="/lifeinthekingdom/avatar"
                      element={<KINGDOM_AVATAR />}
                    />
                    <Route
                      path="/wordfest/avatar"
                      element={<WORDFEST_AVATAR />}
                    />
                    <Route path="/gift/claim/wmc" element={<External />} />
                    <Route exact path="/interests/" element={<Interests />} />
                    <Route
                      exact
                      path="/subscription/v2/checkout"
                      element={<PCDL_Checkout />}
                    />
                    <Route
                      exact
                      path="/subscription/checkout"
                      element={<CheckoutV3 />}
                    />
                    <Route exact path="/checkout" element={<Checkout />} />
                    <Route
                      exact
                      path="/stripe-single"
                      element={<StripeSingle />}
                    />
                    <Route path="/categories" element={<Categories />} />
                    <Route
                      path="/categories/:id/:name"
                      element={<Category />}
                    />
                    <Route
                      path="/series/hGPFmF-VlVI/your-loveworld-specials"
                      element={<SeriesYLWS />}
                    />
                    <Route
                      path="/categories/XR7p8b5icds/languages"
                      element={<Languages />}
                    />
                    <Route
                      path="/categories/languages/:title/:id"
                      element={<Language />}
                    />
                    <Route
                      exact
                      path="/promotionals"
                      element={<Promotionals />}
                    />
                    <Route exact path="/quiz" element={<Quiz />} />
                    <Route exact path="/quiz/:id/" element={<QuizTemplate />} />
                    <Route
                      exact
                      path="/testimony/:id/*"
                      element={<Testimony />}
                    />
                    <Route
                      exact
                      path="/documentary/:id/*"
                      element={<Documentary />}
                    />
                    <Route
                      exact
                      path="/stripe-single"
                      element={<StripeSingle />}
                    />
                    <Route
                      path="/wordfest/special"
                      element={<Wordfest_Special />}
                    />
                    <Route
                      path="/wordfest/special/day/:id"
                      element={<Wordfest_Video_Special />}
                    />
                    <Route
                      path="/wordfest/Extravaganza"
                      element={<Wordfest_Extravaganza />}
                    />
                    <Route path="/series/:id/*" element={<Series />} />
                    <Route
                      path="/series/:id/:title/:lan/:lan_id/*"
                      element={<Series />}
                    />
                    <Route
                      exact
                      path="/pcdl4kids"
                      element={<PCDL4KIDS_HOME />}
                    />
                    <Route
                      path="/special-watch/:id/*"
                      element={<SpecialWatch />}
                    />
                    <Route path="/listen/:id/*" element={<Listen />} />
                    {/* requires auth for media playback? keep if desired */}

                    {/* Protected routes */}
                    <Route
                      path="/"
                      element={
                        isAuthenticated ? (
                          <Home />
                        ) : (
                          <Navigate to="/welcome" replace />
                        )
                      }
                    />
                    <Route
                      path="/everydaysolutions/:id/:name"
                      element={
                        <RequireAuth>
                          <PCDL_SOLUTIONS_PLAYER />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/affiliate/welcome"
                      element={
                        <RequireAuth>
                          <PCDL_AFFILIATE_HOME />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/affiliate/everydaysolutions"
                      element={<PCDL_AFFILIATE_EVERYDAYSOLUTIONS />}
                    />
                    <Route
                      path="/affiliate/onboarding"
                      element={
                        <RequireAuth>
                          <PCDL_AFFILIATE_ONBOARDING />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/affiliate/completion"
                      element={<PCDL_AFFILIATE_COMPLETION />}
                    />

                    <Route
                      path="/affiliate"
                      element={
                        <RequireAuth>
                          <PCDL_AFFILIATE_DASHBOARD />
                        </RequireAuth>
                      }
                    />

                    <Route path="/pco-fc" element={<PCO_FN_REGISTRATION />} />

                    <Route
                      path="/affiliate/terms"
                      element={
                        <RequireAuth>
                          <PCDL_AFFILIATE_TERMS />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/affiliate/everydaysolutions/watch/:id"
                      element={
                        <RequireAuth>
                          <PCDL_AFFILIATE_WATCH />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/everydaysolutions/:id/*"
                      element={
                        <RequireAuth>
                          <PCDL_SOLUTIONS_PLAYER />
                        </RequireAuth>
                      }
                    />
                    <Route path="/courses/buohme" element={<COURSE_BUOM />} />
                    <Route
                      path="/wordfest"
                      element={
                        <RequireAuth>
                          <Wordfest />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/testimony"
                      element={
                        <RequireAuth>
                          <Wordfest_Testimony />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/day/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Video />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/:type/day/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Video />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/quiz/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Quiz />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/help"
                      element={
                        <RequireAuth>
                          <Help />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/expose"
                      element={
                        <RequireAuth>
                          <Expose />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/special/quiz/:id"
                      element={<Wordfest_Quiz_Special />}
                    />
                    <Route
                      path="/wordfest/quiz/app/:id/:bridgeId"
                      element={<Wordfest_Quiz_App />}
                    />
                    <Route
                      path="/challenges/quiz/app/:challenge/:id/:bridgeId"
                      element={<Challenges_Quiz_App />}
                    />
                    <Route
                      path="/wordfest/teens"
                      element={
                        <RequireAuth>
                          <Wordfest_Teens />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/teens/day/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Teens_Video />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/teens/quiz/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Teens_Quiz />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/teens/quiz/app/:id/:bridgeId"
                      element={<Wordfest_Teens_Quiz_App />}
                    />
                    <Route
                      path="/wordfest/pcdl4kids"
                      element={
                        <RequireAuth>
                          <Wordfest_Kids />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/pcdl4kids/day/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Kids_Video />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/pcdl4kids/quiz/:id"
                      element={
                        <RequireAuth>
                          <Wordfest_Kids_Quiz />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/pcdl4kids/quiz/app/:id/:bridgeId"
                      element={<Wordfest_Kids_Quiz_App />}
                    />
                    <Route
                      path="/wordfest/analytics"
                      element={
                        <RequireAuth>
                          <Wordfest_Analytics />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/analytics/group"
                      element={
                        <RequireAuth>
                          <Wordfest_Analytics_group />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact
                      path="/pneuma/wf-admin"
                      element={
                        <RequireAuth>
                          <WordfestAdmin />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact
                      path="/pneuma/ippc-admin"
                      element={
                        <RequireAuth>
                          <IPPCAdmin />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact
                      path="/pneuma/wordatwork"
                      element={
                        <RequireAuth>
                          <WordAtWorkAdmin />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/library"
                      element={
                        <RequireAuth>
                          <Library />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/seraffle/*"
                      element={
                        <RequireAuth>
                          <Raffle />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special-campaign/"
                      element={
                        <RequireAuth>
                          <EXTERNAL_CAMPAIGN />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special-campaign/:id"
                      element={
                        <RequireAuth>
                          <EXTERNAL_CAMPAIGN2 />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/nigat65/"
                      element={
                        <RequireAuth>
                          <NGNCAMPAIGN />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/transform/*"
                      element={
                        <RequireAuth>
                          <IPPC_OFFER />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/deleteaccount"
                      element={
                        <RequireAuth>
                          <DeleteAccount />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/8dom/*"
                      element={
                        <RequireAuth>
                          <DOM_VIDEO />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordfest/certificate"
                      element={
                        <RequireAuth>
                          <CertificateGenerator />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/haven-certificate"
                      element={
                        <RequireAuth>
                          <Haven_Certificate />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/10thoughtsoncompletion/certificate"
                      element={
                        <RequireAuth>
                          <Ten_Thoughts_Certificate />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/captions"
                      element={
                        <RequireAuth>
                          <Captions />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/captions/transcript"
                      element={
                        <RequireAuth>
                          <Transcript />
                        </RequireAuth>
                      }
                    />
                    <Route path="/terms" element={<Terms />} />
                    <Route
                      path="/swc/terms"
                      element={
                        <RequireAuth>
                          <SWCTerms />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/haven-terms"
                      element={
                        <RequireAuth>
                          <HavenTerms />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/hslhsoffer/terms"
                      element={
                        <RequireAuth>
                          <HSLHSTerms />
                        </RequireAuth>
                      }
                    />
                    {/* <Route path="/seraffle/terms" element={<RequireAuth><SERaffleTerms /></RequireAuth>} /> */}
                    <Route
                      path="/seraffle/admin"
                      element={
                        <RequireAuth>
                          <SERaffleAdmin />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/reonoffer/terms"
                      element={
                        <RequireAuth>
                          <REONTerms />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/3mtc/terms"
                      element={
                        <RequireAuth>
                          <MTCTerms />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/iclc/terms"
                      element={
                        <RequireAuth>
                          <ICLCTerms />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ippcoffer/terms"
                      element={
                        <RequireAuth>
                          <IPPCTerms />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ism/"
                      element={
                        <RequireAuth>
                          <PROMO_ISM />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special_offer/celcc6grouplz1"
                      element={
                        <RequireAuth>
                          <PROMO_LAGOSZ6 />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ylws/*"
                      element={
                        <RequireAuth>
                          <YLWS />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wordatwork/*"
                      element={
                        <RequireAuth>
                          <PROMO_WORD_AT_WORK />
                        </RequireAuth>
                      }
                    />

                    {/* Affiliate / refs */}
                    <Route
                      path="/selectgrouplink"
                      element={<AFFILIATE_NEW />}
                    />
                    <Route
                      path="/ref/teens/:zone_id"
                      element={
                        <RequireAuth>
                          <REGISTER_CODE_TEENS />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ref/:code"
                      element={
                        <RequireAuth>
                          <REGISTER_AFFILIATE />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ref/kids/:zone_id"
                      element={
                        <RequireAuth>
                          <REGISTER_CODE_KIDS />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ref/special/:zone_id"
                      element={
                        <RequireAuth>
                          <REGISTER_CODE_KIDS />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/ref/:zone_id/:group_id"
                      element={
                        <RequireAuth>
                          <REGISTER_CODE />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/subscribe/pcdlglobal"
                      element={
                        <RequireAuth>
                          <REGISTER_CODE2 />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact
                      path="/join/:affiliate"
                      element={
                        isAuthenticated ? <SignIn /> : <AffiliateLanding />
                      }
                    />
                    <Route
                      exact
                      path="/join/:affiliate/signup"
                      element={
                        isAuthenticated ? <SignIn /> : <AffiliateSignUp />
                      }
                    />
                    <Route
                      exact
                      path="/tools/affiliate"
                      element={<AFFILIATE />}
                    />
                    <Route
                      exact
                      path="/tools/affiliate/wordfest"
                      element={<AFFILIATE_WORDFEST />}
                    />

                    {/* Payments / Checkout */}
                    <Route
                      path="/subscriptions/payment/tier_gold/nigerian/onetime"
                      element={
                        isAuthenticated ? (
                          <Navigate
                            to="/subscriptions/payment/tier_gold/nigeria/onetime"
                            replace
                          />
                        ) : (
                          <Register />
                        )
                      }
                    />
                    <Route
                      path="/wordfest7"
                      element={
                        isAuthenticated ? (
                          <Navigate to="/wordfest" replace />
                        ) : (
                          <Register />
                        )
                      }
                    />
                    <Route
                      path="/register"
                      element={isAuthenticated ? <SignIn /> : <Register />}
                    />
                    <Route
                      path="/subscriptions/payment/:type/:option"
                      element={
                        <RequireAuth>
                          <Payment />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special_offer/lwukzonec/*"
                      element={
                        <RequireAuth>
                          <PROMO_SWC />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special_offer/cemeseasia"
                      element={
                        <RequireAuth>
                          <PROMO_CEMESEASIA />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/pcdl_offer"
                      element={
                        <RequireAuth>
                          <PCDL_OFFER />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact
                      path="/subscriptions/payment/complete/:transactionId"
                      element={
                        <RequireAuth>
                          <Complete />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/1bs"
                      element={
                        <RequireAuth>
                          <PaymentV2 />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/reonoffer"
                      element={
                        <RequireAuth>
                          <PaymentV2_REON />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/SA-gift"
                      element={
                        <RequireAuth>
                          <PaymentV2SA />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/subscriptions/payment/:type/"
                      element={
                        <RequireAuth>
                          <PaymentOneTime />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/subscription/*"
                      element={
                        <RequireAuth>
                          <PCDL_Subscription />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special-offer/:special"
                      element={
                        <RequireAuth>
                          <PCDL_Subscription_Special />
                        </RequireAuth>
                      }
                    />

                    {/* Content / media */}
                    <Route path="/watch/:id/*" element={<Watch />} />
                    <Route
                      path="/watchlist"
                      element={
                        <RequireAuth>
                          <Watchlist />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/playlists/create"
                      element={
                        <RequireAuth>
                          <Playlist_Create />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/watchlist/video/:id/:name"
                      element={
                        <RequireAuth>
                          <Video />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/watchlist/audio/:id/:name"
                      element={
                        <RequireAuth>
                          <Audio />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/sodlv2/:id/:referrer"
                      element={<SodlV2 />}
                    />
                    <Route
                      path="/sodl/:id"
                      element={
                        <RequireAuth>
                          <Sodl />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/search"
                      element={
                        <RequireAuth>
                          <PCDL_SEARCH />
                        </RequireAuth>
                      }
                    />
                    <Route path="/results" element={<Results />} />

                    {/* Coupons / Redeem */}
                    <Route path="/haven-coupon" element={<Haven_Redeem />} />
                    <Route
                      path="/4mot-redeem/*"
                      element={
                        <RequireAuth>
                          <MOT_Redeem />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/redeem/coupon"
                      element={
                        <RequireAuth>
                          <Coupon_Redeem />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/redeem/v2/coupon"
                      element={
                        <RequireAuth>
                          <COUPON_CODES />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/exclusive"
                      element={
                        <RequireAuth>
                          <COUPON_CODES />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/itplc2026"
                      element={
                        <RequireAuth>
                          <ITPLC_REDEEM />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/coupons"
                      element={
                        <RequireAuth>
                          <Coupon_Fetch />
                        </RequireAuth>
                      }
                    />

                    {/* Challenges */}
                    <Route
                      path="/4mot/*"
                      element={
                        <RequireAuth>
                          <CHALLENGE_4MOT />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/4mot/:timestamp/:item_id"
                      element={
                        <RequireAuth>
                          <CHALLENGE_4MOT_WATCH />
                        </RequireAuth>
                      }
                    />
                    {/* <Route path="/4mot/testimony/" element={<Testimonies />} /> */}

                    {/* Offers / Campaigns */}
                    <Route
                      path="/special-campaign/"
                      element={
                        <RequireAuth>
                          <EXTERNAL_CAMPAIGN />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special-campaign/:id"
                      element={
                        <RequireAuth>
                          <EXTERNAL_CAMPAIGN2 />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/special-offer"
                      element={
                        <RequireAuth>
                          <Special />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/globalcho247"
                      element={
                        <Navigate to="/referral/x6w/kc/globalecho" replace />
                      }
                    />
                    <Route
                      path="/referral/:dest/:ref/:code"
                      element={<ReferralRedirect />}
                    />
                    <Route
                      path="/referral/:dest/:ref"
                      element={<ReferralRedirect />}
                    />
                    <Route
                      path="/referral/:dest"
                      element={<ReferralRedirect />}
                    />
                    <Route
                      path="/giftoffer/participation"
                      element={
                        <RequireAuth>
                          <ChristmasParticipation />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/giftoffer/terms"
                      element={
                        <RequireAuth>
                          <ChristmasOfferTerms />
                        </RequireAuth>
                      }
                    />
                    {/* <Route path="/giftoffer/*" element={<RequireAuth><ChristmasOffer /></RequireAuth>} /> */}

                    {/* Wallet / History / Settings */}
                    <Route
                      exact
                      path="/history"
                      element={
                        <RequireAuth>
                          <History />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/wallet"
                      element={
                        <RequireAuth>
                          <Wallet />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RequireAuth>
                          <Settings />
                        </RequireAuth>
                      }
                    />

                    {/* Reon / Analytics */}
                    <Route
                      path="/reon/analytics"
                      element={
                        <RequireAuth>
                          <AnalyticsReon />
                        </RequireAuth>
                      }
                    />
                    {/* Worth Hearing New Page */}
                    <Route
                      path="/may-newsletter"
                      element={
                        // <RequireAuth>
                        <WorthHearing />
                        // </RequireAuth>
                      }
                    />
                    {/* Womans Page */}
                    <Route path="/women" element={<WomansPage />} />
                    {/* Newtitles Page */}
                    <Route path="/New-Releases" element={<NewTitles />} />

                    {/* Default 404 */}
                    <Route path="*" element={<PageNotFound />} />
                  </CustomSwitch>
                </Suspense>
              </div>
              <Footer />
            </Router>
          </ThemeProvider>
        </Context.Provider>
      </ErrorBoundary>
    </Provider>
  );
}

export default function App() {
  const raw = localStorage.getItem('pcdl.store');

  Intercom({
    app_id: 'ce0df913',
  });

  const inMaintenance = false; // flip from config/feature flag/server signal
  if (inMaintenance) {
    return (
      <MaintenancePage
        title="PCDL is undergoing maintenance"
        message="We’re tuning things up to serve you better."
        eta={Date.now() + 1000 * 60 * 200} // ~45 minutes from now (optional)
        statusUrl="https://status.pcdl.co" // optional
        contactEmail="support@pcdl.co"
        onRetry={() => {
          // Optionally ping your health endpoint before unblocking:
          // return fetch('/healthz').then(r => { if (r.ok) window.location.reload(); });
          window.location.reload();
        }}
        refreshIntervalMs={0} // set e.g. 30000 to auto-retry every 30s
      />
    );
  }

  return (
    <div className="App relative">
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </div>
  );
}
